import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api/api.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import {
  BackupBannerHeader,
  BackupListSection,
  DeleteBackupModal,
  RestoreBackupModal,
} from './database-backup/index.js';

export const DatabaseBackupView: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useNotificationStore();

  const [selectedRestoreFile, setSelectedRestoreFile] = useState<string | null>(null);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [uploadedDumpContent, setUploadedDumpContent] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const [deletingFilename, setDeletingFilename] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Query server backup snapshots list
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['adminDatabaseBackups'],
    queryFn: () => api.listDatabaseBackups(),
  });

  const backups = data?.backups || [];

  // Create immediate backup mutation
  const createMutation = useMutation({
    mutationFn: () => api.createDatabaseBackup(),
    onSuccess: (res) => {
      showToast('Backup Berhasil', res.message || 'Snapshot database berhasil dibuat.', 'success');
      queryClient.invalidateQueries({ queryKey: ['adminDatabaseBackups'] });
    },
    onError: (err: any) => {
      showToast('Gagal Dibuat', err.message || 'Gagal membuat backup database.', 'error');
    },
  });

  // Delete backup file mutation
  const deleteMutation = useMutation({
    mutationFn: (filename: string) => api.deleteDatabaseBackup(filename),
    onSuccess: (res) => {
      showToast('Backup Dihapus', res.message || 'File backup berhasil dihapus.', 'success');
      setIsDeleteModalOpen(false);
      setDeletingFilename(null);
      queryClient.invalidateQueries({ queryKey: ['adminDatabaseBackups'] });
    },
    onError: (err: any) => {
      showToast('Gagal Menghapus', err.message || 'Tidak dapat menghapus file backup.', 'error');
    },
  });

  // Restore database mutation
  const restoreMutation = useMutation({
    mutationFn: (payload: { filename?: string; dumpData?: any }) => api.restoreDatabase(payload),
    onSuccess: (res) => {
      showToast(
        'Database Dipulihkan',
        res.message || 'Seluruh data SQLite berhasil dipulihkan dari backup.',
        'success'
      );
      setIsRestoreModalOpen(false);
      setSelectedRestoreFile(null);
      setUploadedDumpContent(null);
      setUploadedFileName(null);
      queryClient.invalidateQueries();
    },
    onError: (err: any) => {
      showToast('Gagal Pemulihan', err.message || 'Proses restore database gagal.', 'error');
    },
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      showToast('Format Salah', 'File restore harus berupa berkas dump JSON (.json)', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const parsed = JSON.parse(text);
        if (!parsed.data || !parsed.data.users || !parsed.data.books) {
          throw new Error('Skema JSON backup tidak memiliki struktur data UIBN yang valid.');
        }
        setUploadedDumpContent(text);
        setUploadedFileName(file.name);
        setSelectedRestoreFile(null);
        setIsRestoreModalOpen(true);
      } catch (err: any) {
        showToast('Berkas Rusak', err.message || 'Berkas JSON backup tidak valid.', 'error');
      }
    };
    reader.readAsText(file);
  };

  const handleConfirmRestore = () => {
    if (uploadedDumpContent) {
      restoreMutation.mutate({ dumpData: JSON.parse(uploadedDumpContent) });
    } else if (selectedRestoreFile) {
      restoreMutation.mutate({ filename: selectedRestoreFile });
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview Card Banner */}
      <BackupBannerHeader
        isCreating={createMutation.isPending}
        onCreateBackup={() => createMutation.mutate()}
        onFileUpload={handleFileUpload}
      />

      {/* Backup Files List Section */}
      <BackupListSection
        backups={backups}
        isLoading={isLoading}
        isFetching={isFetching}
        isDeleting={deleteMutation.isPending}
        onRefetch={() => refetch()}
        onInitiateRestore={(filename) => {
          setSelectedRestoreFile(filename);
          setUploadedDumpContent(null);
          setUploadedFileName(null);
          setIsRestoreModalOpen(true);
        }}
        onInitiateDelete={(filename) => {
          setDeletingFilename(filename);
          setIsDeleteModalOpen(true);
        }}
      />

      {/* Delete Backup Confirmation Modal */}
      <DeleteBackupModal
        isOpen={isDeleteModalOpen}
        filename={deletingFilename}
        isDeleting={deleteMutation.isPending}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingFilename(null);
        }}
        onConfirmDelete={(filename) => deleteMutation.mutate(filename)}
      />

      {/* Restore Warning Confirmation Modal */}
      <RestoreBackupModal
        isOpen={isRestoreModalOpen}
        filename={uploadedFileName || selectedRestoreFile}
        isRestoring={restoreMutation.isPending}
        onClose={() => setIsRestoreModalOpen(false)}
        onConfirmRestore={handleConfirmRestore}
      />
    </div>
  );
};
